import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { fetchConversations, setFilters, selectConversation, socketUpsertConversation, removeConversation } from '../../store/slices/conversationsSlice';
import { fetchMessages, sendMessage, markRead, socketAppendMessage } from '../../store/slices/messagesSlice';
import { connectSocket, onSocketEvents, offSocketEvents, joinConversationRoom, leaveConversationRoom, sendTyping } from '../../services/socket';
import conversationService from '../../services/conversationService';
import ConversationList from '../../components/messages/ConversationList';
import ConversationHeader from '../../components/messages/ConversationHeader';
import ThreadView from '../../components/messages/ThreadView';
import Composer from '../../components/messages/Composer';
import RightPanel from '../../components/messages/RightPanel';
import './AdminMessages.css';

const AdminMessages = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector(s => s.auth, shallowEqual);
  const conversations = useSelector(s => s.conversations.list, shallowEqual);
  const convoFilters = useSelector(s => s.conversations.filters, shallowEqual);
  const selectedId = useSelector(s => s.conversations.selectedId);
  const byConv = useSelector(s => s.supportMessages.byConv, shallowEqual);
  const messagesBucket = byConv[selectedId || ''] || { items: [] };
  const [typingMap, setTypingMap] = useState({}); // { [conversationId]: boolean }
  const [isListOpen, setIsListOpen] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newWholesalerId, setNewWholesalerId] = useState('');
  const [newWholesalerEmail, setNewWholesalerEmail] = useState('');
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isAdminToAdmin, setIsAdminToAdmin] = useState(false);
  const [adminPeerId, setAdminPeerId] = useState('');
  const [adminPeerEmail, setAdminPeerEmail] = useState('');
  const [newTags, setNewTags] = useState('');
  const prevSelectedRef = useRef(null);
  const typingTimersRef = useRef({}); // { [conversationId]: timeoutId }

  // Memoized socket handlers
  const handleConversationCreated = useMemo(() => (c) => dispatch(socketUpsertConversation(c)), [dispatch]);
  const handleConversationUpdated = useMemo(() => (c) => dispatch(socketUpsertConversation(c)), [dispatch]);
  const handleMessageCreated = useMemo(() => (msg) => {
    const conversationId = msg?.conversation || msg?.conversationId || selectedId;
    dispatch(socketAppendMessage({ conversationId, message: msg }));
  }, [dispatch, selectedId]);
  const handleTyping = useMemo(() => ({ conversationId, isTyping, userId }) => {
    if (!conversationId) return;
    // Ignore self typing events just in case, though server doesn't echo back to sender
    if (userId && user && String(userId) === String(user._id)) return;
    setTypingMap(prev => ({ ...prev, [conversationId]: !!isTyping }));
    // Manage a per-conversation debounce to auto-clear after 3s since last 'true'
    const timers = typingTimersRef.current || {};
    if (timers[conversationId]) {
      clearTimeout(timers[conversationId]);
      delete timers[conversationId];
    }
    if (isTyping) {
      timers[conversationId] = setTimeout(() => {
        setTypingMap(curr => ({ ...curr, [conversationId]: false }));
        delete timers[conversationId];
      }, 3000);
    }
    typingTimersRef.current = timers;
  }, [user]);
  const handleConversationDeleted = useMemo(() => ({ conversationId }) => {
    if (!conversationId) return;
    dispatch(removeConversation(conversationId));
  }, [dispatch]);

  // Connect socket and register handlers
  useEffect(() => {
    if (!token) return;
    connectSocket(token);
    const handlers = {
      onConversationCreated: handleConversationCreated,
      onConversationUpdated: handleConversationUpdated,
      onMessageCreated: handleMessageCreated,
      onTyping: handleTyping,
    };
    // Manually bind deleted event as it's not yet wired in socket service helper
    const s = connectSocket(token);
    if (s && handleConversationDeleted) s.on('conversation:deleted', handleConversationDeleted);
    onSocketEvents(handlers);
    return () => {
      if (s && handleConversationDeleted) s.off('conversation:deleted', handleConversationDeleted);
      offSocketEvents(handlers);
    };
  }, [token, handleConversationCreated, handleConversationUpdated, handleMessageCreated, handleTyping, handleConversationDeleted]);

  // Fetch conversations on mount and when filters change
  useEffect(() => {
    dispatch(fetchConversations(convoFilters));
  }, [dispatch, convoFilters]);

  // Auto-select first conversation when list is loaded and nothing is selected
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      dispatch(selectConversation(conversations[0]._id));
    }
  }, [conversations, selectedId, dispatch]);

  // When a conversation is selected, join room, fetch messages, and mark read
  useEffect(() => {
    const prev = prevSelectedRef.current;
    if (prev && prev !== selectedId) {
      leaveConversationRoom(prev);
    }
    if (selectedId) {
      joinConversationRoom(selectedId);
      dispatch(fetchMessages({ conversationId: selectedId, page: 1, limit: 50 }));
      dispatch(markRead({ conversationId: selectedId }));
    }
    prevSelectedRef.current = selectedId;
  }, [selectedId, dispatch]);

  const selectedConversation = useMemo(
    () => conversations.find(c => c._id === selectedId) || null,
    [conversations, selectedId]
  );

  // Auto-open drawer on small screens when no conversation is selected
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    if (isMobile && !selectedId) {
      setIsListOpen(true);
    }
  }, [selectedId]);

  const onSend = async ({ channel, text, internal }) => {
    if (!selectedId || !text?.trim()) return;
    await dispatch(sendMessage({ conversationId: selectedId, payload: { channel, internal, bodyText: text } }));
  };

  const onTyping = (isTyping) => {
    if (!selectedId) return;
    sendTyping(selectedId, isTyping);
  };

  const canCreate = user?.accountType === 'ADMIN';
  const openNewModal = () => setShowNewModal(true);
  const closeNewModal = () => {
    setShowNewModal(false);
    setNewSubject('');
    setNewWholesalerId('');
    setNewWholesalerEmail('');
    setNewCustomerId('');
    setNewCustomerEmail('');
    setCreateError('');
    setIsAdminToAdmin(false);
    setAdminPeerId('');
    setAdminPeerEmail('');
    setNewTags('');
  };

  // Mobile helpers
  const openList = () => setIsListOpen(true);
  const closeList = () => setIsListOpen(false);
  const onSelectConversation = (id) => {
    dispatch(selectConversation(id));
    closeList();
  };
  const createConversation = async () => {
    setCreateError('');
    if (!newSubject.trim()) { setCreateError('Subject is required'); return; }
    try {
      setCreating(true);
      const payload = { subject: newSubject.trim() };
      if (newTags.trim()) payload.tags = newTags.trim();
      if (isAdminToAdmin) {
        payload.inbox = 'admin';
        if (adminPeerId.trim()) payload.adminPeerId = adminPeerId.trim();
        if (adminPeerEmail.trim()) payload.adminPeerEmail = adminPeerEmail.trim();
        if (!payload.adminPeerId && !payload.adminPeerEmail) { setCreateError('Provide admin peer ID or Email'); setCreating(false); return; }
      } else {
        const hasWhs = !!newWholesalerId.trim() || !!newWholesalerEmail.trim();
        const hasCust = !!newCustomerId.trim() || !!newCustomerEmail.trim();
        if (!hasWhs || !hasCust) { setCreateError('Provide WHS (ID or Email) and RTL (ID or Email)'); setCreating(false); return; }
        if (newWholesalerId.trim()) payload.wholesalerId = newWholesalerId.trim();
        if (newWholesalerEmail.trim()) payload.wholesalerEmail = newWholesalerEmail.trim();
        if (newCustomerId.trim()) payload.customerId = newCustomerId.trim();
        if (newCustomerEmail.trim()) payload.customerEmail = newCustomerEmail.trim();
      }
      const c = await conversationService.create(payload);
      // Refresh list and select new conversation; ensure admin inbox visible
      if (payload.inbox === 'admin') {
        const newFilters = { ...convoFilters, inbox: 'admin', page: 1 };
        dispatch(setFilters(newFilters));
        await dispatch(fetchConversations(newFilters));
      } else {
        await dispatch(fetchConversations(convoFilters));
      }
      dispatch(selectConversation(c._id));
      closeNewModal();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create conversation', e);
      setCreateError(e?.response?.data?.message || e?.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={"admin-messages" + (isListOpen ? ' drawer-open' : '')}>
      <div className="admin-messages__overlay" onClick={closeList} aria-hidden="true" />
      <aside id="inbox-drawer" className="admin-messages__left">
        <div className="admin-messages__left-header">
          <div className="admin-messages__left-toolbar">
            <h2 style={{ flex:'0 0 auto' }}>Inbox</h2>
            {canCreate && (
              <button aria-label="New Conversation" onClick={openNewModal} className="btn btn-primary" style={{ marginLeft: 8, flex:'0 0 auto' }}>New Conversation</button>
            )}
          </div>
          <div className="filters">
            <div className="filter filter--search">
              <label className="filter__label" htmlFor="filter-search">Search</label>
              <input
                id="filter-search"
                placeholder="Search subjects or customers"
                aria-label="Search"
                value={convoFilters.q || ''}
                onChange={(e) => dispatch(setFilters({ q: e.target.value, page: 1 }))}
              />
            </div>
            <div className="filter filter--status">
              <label className="filter__label" htmlFor="filter-status">Status</label>
              <select id="filter-status" aria-label="Status" value={convoFilters.status || ''} onChange={(e) => dispatch(setFilters({ status: e.target.value || undefined, page: 1 }))}>
                <option value="">All</option>
                <option>Open</option>
                <option>Pending</option>
                <option>On Hold</option>
                <option>Closed</option>
              </select>
            </div>
            <div className="filter filter--assignee">
              <label className="filter__label" htmlFor="filter-assignee">Assignee</label>
              <select id="filter-assignee" aria-label="Assignee" value={convoFilters.assignee || ''} onChange={(e)=> dispatch(setFilters({ assignee: e.target.value || undefined, page:1 }))}>
                <option value="">All</option>
                <option value="me">Assigned to me</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
            <div className="filter filter--inbox">
              <label className="filter__label" htmlFor="filter-inbox">Inbox</label>
              <select id="filter-inbox" aria-label="Inbox" value={convoFilters.inbox || 'support'} onChange={(e)=> dispatch(setFilters({ inbox: e.target.value || undefined, page:1 }))}>
                <option value="support">Support</option>
                <option value="sales">Sales</option>
                <option value="billing">Billing</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="filter filter--tags">
              <label className="filter__label" htmlFor="filter-tags">Tags (comma-separated)</label>
              <input
                id="filter-tags"
                placeholder="e.g. urgent, vip"
                aria-label="Tags (comma-separated)"
                value={convoFilters.tags || ''}
                onChange={(e)=> dispatch(setFilters({ tags: e.target.value || undefined, page:1 }))}
              />
            </div>
          </div>
        </div>
        <ConversationList
          items={conversations}
          selectedId={selectedId}
          onSelect={onSelectConversation}
          onClose={closeList}
        />
      </aside>
      <main className="admin-messages__main">
        {/* Mobile top bar for opening inbox drawer (mobile-only via CSS) */}
        <div className="mobile-topbar show-on-mobile" style={{ padding:'8px 12px', borderBottom:'1px solid #eee', justifyContent:'flex-end' }}>
          <button
            onClick={openList}
            aria-label="Open inbox"
            aria-controls="inbox-drawer"
            aria-expanded={isListOpen}
            style={{ padding:'6px 10px', border:'1px solid #ddd', background:'#fff', borderRadius:6 }}
          >
            Inbox
          </button>
        </div>
        {selectedConversation ? (
          <>
            <ConversationHeader
              conversation={selectedConversation}
              currentUser={user}
              onDeleted={(id)=> dispatch(removeConversation(id))}
              showBack
              onBack={openList}
            />
            <ThreadView
              messages={messagesBucket.items}
              currentUser={user}
              typing={!!typingMap[selectedId]}
            />
            <Composer onSend={onSend} onTyping={onTyping} />
          </>
        ) : (
          <div className="admin-messages__empty" style={{ textAlign:'center' }}>
            <div style={{ marginBottom:12, color:'#6b7280' }}>Select a conversation to view messages</div>
            <button
              className="show-on-mobile"
              onClick={openList}
              aria-label="Open inbox"
              aria-controls="inbox-drawer"
              aria-expanded={isListOpen}
              style={{ padding:'8px 12px', border:'1px solid #ddd', background:'#fff', borderRadius:8 }}
            >
              Open Inbox
            </button>
          </div>
        )}
      </main>
      {selectedConversation && (
        <aside className="admin-messages__right">
          <RightPanel conversation={selectedConversation} />
        </aside>
      )}

      {showNewModal && (
        <div className="modal-backdrop" onClick={closeNewModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Conversation</h3>
            <div className="modal-grid">
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={isAdminToAdmin} onChange={(e)=> setIsAdminToAdmin(e.target.checked)} />
                Admin ↔ Admin conversation
              </label>
              <input placeholder="Subject" value={newSubject} onChange={(e)=> setNewSubject(e.target.value)} />
              {isAdminToAdmin ? (
                <>
                  <input placeholder="Admin Peer ID" value={adminPeerId} onChange={(e)=> setAdminPeerId(e.target.value)} />
                  <input placeholder="Admin Peer Email (optional)" value={adminPeerEmail} onChange={(e)=> setAdminPeerEmail(e.target.value)} />
                </>
              ) : (
                <>
                  <input placeholder="Wholesaler ID (WHS)" value={newWholesalerId} onChange={(e)=> setNewWholesalerId(e.target.value)} />
                  <input placeholder="Wholesaler Email (optional)" value={newWholesalerEmail} onChange={(e)=> setNewWholesalerEmail(e.target.value)} />
                  <input placeholder="Customer ID (RTL)" value={newCustomerId} onChange={(e)=> setNewCustomerId(e.target.value)} />
                  <input placeholder="Customer Email (optional)" value={newCustomerEmail} onChange={(e)=> setNewCustomerEmail(e.target.value)} />
                </>
              )}
              <input placeholder="Tags (comma-separated)" value={newTags} onChange={(e)=> setNewTags(e.target.value)} aria-label="Tags (comma-separated)" />
              {createError && <div style={{ color: '#b91c1c', fontSize: 12 }}>{createError}</div>}
            </div>
            <div className="modal-actions">
              <button onClick={closeNewModal} disabled={creating}>Cancel</button>
              <button onClick={createConversation} disabled={creating || !newSubject}>
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
